// Renders the user's "Your data" summary onto an offscreen canvas as a
// nice printable/shareable PNG card. Same visual language as the book
// share card (cream background, serif R mark, mono metadata).
const W = 1080;
const H = 1350;

const LABELS = {
  books_added: "Books added",
  reviews: "Reviews",
  wishlist: "Wishlist",
  lists_sections: "Custom lists",
  author_follows: "Authors followed",
  author_reviews: "Author reviews",
  payment_transactions: "Payments",
  email_log: "Emails received",
};

export async function renderDataCard(payload) {
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Cream background + subtle grain.
  ctx.fillStyle = "#F0EAD6";
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "rgba(10,10,10,0.02)";
  for (let i = 0; i < H + W; i += 6) ctx.fillRect(0, i, W, 1);

  // Inset border.
  const pad = 64;
  ctx.strokeStyle = "rgba(10,10,10,0.14)";
  ctx.lineWidth = 2;
  ctx.strokeRect(pad, pad, W - pad * 2, H - pad * 2);

  // Header: R mark top-left, READS + generated date top-right.
  ctx.fillStyle = "#0A0A0A";
  ctx.font = "800 56px 'Playfair Display', Georgia, serif";
  ctx.textAlign = "left";
  ctx.fillText("R", pad + 30, pad + 76);
  ctx.font = "500 14px 'IBM Plex Mono', monospace";
  ctx.fillStyle = "#4A4030";
  ctx.textAlign = "right";
  const generatedAt = new Date(payload.generated_at || Date.now());
  ctx.fillText("READSCO.ORG", W - pad - 30, pad + 42);
  ctx.fillText(
    generatedAt.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }).toUpperCase(),
    W - pad - 30,
    pad + 68
  );

  // Divider under header.
  ctx.strokeStyle = "rgba(10,10,10,0.14)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(pad + 30, pad + 110);
  ctx.lineTo(W - pad - 30, pad + 110);
  ctx.stroke();

  // Eyebrow + title.
  ctx.textAlign = "left";
  ctx.fillStyle = "#4A4030";
  ctx.font = "500 18px 'IBM Plex Mono', monospace";
  ctx.fillText("YOUR DATA · GDPR SUMMARY", pad + 30, pad + 160);

  const acct = payload.account || {};
  const name = acct.name || "Reader";
  ctx.fillStyle = "#0A0A0A";
  ctx.font = "800 78px 'Playfair Display', Georgia, serif";
  ctx.fillText(name, pad + 30, pad + 240);

  ctx.fillStyle = "#4A4030";
  ctx.font = "italic 26px 'Playfair Display', Georgia, serif";
  ctx.fillText(acct.email || "", pad + 30, pad + 285);

  // "Member since" line.
  if (acct.created_at) {
    const joined = new Date(acct.created_at);
    ctx.fillStyle = "#525252";
    ctx.font = "500 14px 'IBM Plex Mono', monospace";
    ctx.fillText(
      `MEMBER SINCE ${joined.toLocaleDateString(undefined, { month: "short", year: "numeric" }).toUpperCase()}`,
      pad + 30,
      pad + 318
    );
  }

  // Stat grid — 2×4 tiles.
  const gridTop = pad + 380;
  const gridLeft = pad + 30;
  const gridRight = W - pad - 30;
  const cols = 2;
  const rows = 4;
  const cellW = (gridRight - gridLeft) / cols;
  const cellH = 140;

  const entries = Object.entries(LABELS)
    .map(([k, label]) => [label, Number(payload.summary?.[k] ?? 0)])
    .slice(0, cols * rows);

  entries.forEach(([label, count], i) => {
    const c = i % cols;
    const r = Math.floor(i / cols);
    const x = gridLeft + c * cellW;
    const y = gridTop + r * cellH;
    // Cell fill + inner border.
    ctx.fillStyle = "#FAF5E6";
    ctx.fillRect(x, y, cellW - 12, cellH - 12);
    ctx.strokeStyle = "rgba(10,10,10,0.10)";
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, cellW - 12, cellH - 12);
    // Label
    ctx.fillStyle = "#525252";
    ctx.font = "500 13px 'IBM Plex Mono', monospace";
    ctx.textAlign = "left";
    ctx.fillText(label.toUpperCase(), x + 20, y + 34);
    // Count.
    ctx.fillStyle = "#0A0A0A";
    ctx.font = "800 64px 'Playfair Display', Georgia, serif";
    ctx.fillText(String(count), x + 20, y + 100);
  });

  // Footer note.
  ctx.fillStyle = "#4A4030";
  ctx.font = "italic 20px 'Playfair Display', Georgia, serif";
  ctx.textAlign = "center";
  wrap(
    ctx,
    "This card shows everything Reads has stored about you. Under GDPR you can request the full machine-readable export or delete your account any time from the Account page.",
    W / 2,
    H - pad - 130,
    W - pad * 2 - 120,
    28
  );

  ctx.fillStyle = "#525252";
  ctx.font = "500 14px 'IBM Plex Mono', monospace";
  ctx.fillText("READSCO.ORG · YOUR DATA CARD", W / 2, H - pad - 32);

  return canvas.toDataURL("image/png");
}

function wrap(ctx, text, cx, y, maxWidth, lineHeight) {
  const words = text.split(/\s+/);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  lines.forEach((ln, i) => ctx.fillText(ln, cx, y + i * lineHeight));
}
